/* eslint-disable react/no-array-index-key, react/require-default-props */
import React from 'react';
import { Document, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';

import { proposalFilename, proposalTableLayout } from './docx';
import type { ProposalTableLayout } from './layout';
import type {
  DeclarationAlign,
  DeclarationParagraph,
  DeclarationRun,
  DeclarationStructure,
} from './declaration';
import type {
  ProposalDocumentAlign,
  ProposalDocumentField,
  ProposalDocumentSection,
  ProposalDocumentSignature,
  ProposalDocumentStructure,
  ProposalDocumentTable,
} from './structure';

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 48,
    paddingHorizontal: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#000000',
    lineHeight: 1.35,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginTop: 12,
    marginBottom: 6,
  },
  groupTitle: {
    fontFamily: 'Helvetica-Bold',
    marginTop: 6,
    marginBottom: 3,
  },
  field: {
    marginBottom: 3,
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  table: {
    borderTopWidth: 0.5,
    borderLeftWidth: 0.5,
    borderColor: '#000000',
  },
  row: {
    flexDirection: 'row',
  },
  cell: {
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: '#000000',
    paddingVertical: 2,
    paddingHorizontal: 3,
  },
  headerCell: {
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#EEEEEE',
    textAlign: 'center',
  },
  signatureDate: {
    marginTop: 12,
    marginBottom: 36,
  },
  signatureLine: {
    textAlign: 'center',
  },
  signatureText: {
    textAlign: 'center',
  },
});

const TEXT_ALIGN: Record<ProposalDocumentAlign, 'left' | 'right' | 'center'> = {
  esquerda: 'left',
  direita: 'right',
  centro: 'center',
};

const percentages = (pesos: number[]) => {
  const soma = pesos.reduce((acc, peso) => acc + peso, 0) || 1;
  return pesos.map((peso) => `${(peso * 100) / soma}%`);
};

const Field: React.FC<{ field: ProposalDocumentField }> = ({ field }) => (
  <Text style={styles.field}>
    <Text style={styles.bold}>{`${field.rotulo}: `}</Text>
    {field.valor}
  </Text>
);

const ItemsTable: React.FC<{
  table: ProposalDocumentTable;
  layout: ProposalTableLayout;
  repeatHeader: boolean;
}> = ({ table, layout, repeatHeader }) => {
  const widths = percentages(table.pesos);
  return (
    <View style={[styles.table, { fontSize: layout.fontPt }]}>
      {/* `fixed` repete o cabeçalho em cada página gerada pelo mesmo <Page> (só a tabela longa). */}
      <View style={styles.row} wrap={false} fixed={repeatHeader}>
        {table.colunas.map((coluna, i) => (
          <Text key={i} style={[styles.cell, styles.headerCell, { width: widths[i] }]}>
            {coluna}
          </Text>
        ))}
      </View>
      {table.linhas.map((linha, r) => (
        // Linha inteira numa página só; a tabela continua na página seguinte.
        <View key={r} style={styles.row} wrap={false}>
          {linha.map((valor, i) => (
            <Text
              key={i}
              style={[
                styles.cell,
                { width: widths[i], textAlign: TEXT_ALIGN[table.alinhamentos[i] ?? 'esquerda'] },
              ]}
            >
              {valor}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};

const Section: React.FC<{
  section: ProposalDocumentSection;
  numero: number;
  layout: ProposalTableLayout;
  repeatHeader?: boolean;
}> = ({ section, numero, layout, repeatHeader = false }) => (
  <View>
    <Text style={styles.sectionTitle}>{`${numero}. ${section.titulo.toUpperCase()}`}</Text>
    {(section.campos ?? []).map((campo, i) => (
      <Field key={`c${i}`} field={campo} />
    ))}
    {(section.grupos ?? []).map((grupo, g) => (
      <View key={`g${g}`}>
        <Text style={styles.groupTitle}>{grupo.titulo}</Text>
        {grupo.campos.map((campo, i) => (
          <Field key={i} field={campo} />
        ))}
      </View>
    ))}
    {(section.paragrafos ?? []).map((texto, i) => (
      <Text key={`p${i}`} style={styles.field}>
        {texto}
      </Text>
    ))}
    {section.tabela &&
      (section.tabela.linhas.length ? (
        <ItemsTable table={section.tabela} layout={layout} repeatHeader={repeatHeader} />
      ) : (
        <Text>Nenhum item selecionado.</Text>
      ))}
  </View>
);

// Exportados para o PDF de cada declaração.
export const PdfDocumentHeader: React.FC<{ titulo: string }> = ({ titulo }) => (
  <Text style={styles.title}>{titulo}</Text>
);

export const PdfSignatureBlock: React.FC<{ assinatura: ProposalDocumentSignature }> = ({
  assinatura,
}) => (
  <View wrap={false}>
    <Text style={styles.signatureDate}>{`${assinatura.local}, ${assinatura.data}.`}</Text>
    <Text style={styles.signatureLine}>_______________________________________</Text>
    <Text style={[styles.signatureText, styles.bold]}>{assinatura.nome}</Text>
    <Text style={styles.signatureText}>{`CPF: ${assinatura.cpf}`}</Text>
    <Text style={styles.signatureText}>{`Cargo: ${assinatura.cargo}`}</Text>
  </View>
);

// Acima disto a tabela vai num <Page> próprio para repetir o cabeçalho em cada página; até aqui
// segue no meio do texto e a proposta curta não ganha páginas.
export const LONG_TABLE_ROWS = 15;

export const ProposalPdfDocument: React.FC<{ structure: ProposalDocumentStructure }> = ({
  structure,
}) => {
  const layout = proposalTableLayout(structure);
  const pageProps = { size: 'A4' as const, orientation: layout.orientation, style: styles.page };
  const indiceTabela = structure.secoes.findIndex((secao) => secao.tabela);
  const longa =
    indiceTabela >= 0 &&
    (structure.secoes[indiceTabela].tabela?.linhas.length ?? 0) > LONG_TABLE_ROWS;

  const renderSections = (inicio: number, fim: number) =>
    structure.secoes
      .slice(inicio, fim)
      .map((secao, offset) => (
        <Section
          key={inicio + offset}
          section={secao}
          numero={inicio + offset + 1}
          layout={layout}
          repeatHeader={longa && inicio + offset === indiceTabela}
        />
      ));

  // Título e assinatura sempre juntos na mesma página.
  const fechamento = (
    <View wrap={false}>
      <Text style={styles.sectionTitle}>
        {`${structure.secoes.length + 1}. LOCAL, DATA E ASSINATURA`}
      </Text>
      <PdfSignatureBlock assinatura={structure.assinatura} />
    </View>
  );

  if (!longa) {
    return (
      <Document title={structure.titulo} creator="ConLicitação">
        <Page {...pageProps}>
          <PdfDocumentHeader titulo={structure.titulo} />
          {renderSections(0, structure.secoes.length)}
          {fechamento}
        </Page>
      </Document>
    );
  }

  return (
    <Document title={structure.titulo} creator="ConLicitação">
      <Page {...pageProps}>
        <PdfDocumentHeader titulo={structure.titulo} />
        {renderSections(0, indiceTabela)}
      </Page>
      <Page {...pageProps}>{renderSections(indiceTabela, indiceTabela + 1)}</Page>
      <Page {...pageProps}>
        {renderSections(indiceTabela + 1, structure.secoes.length)}
        {fechamento}
      </Page>
    </Document>
  );
};

export const downloadProposalPdf = async (structure: ProposalDocumentStructure) => {
  const blob = await pdf(<ProposalPdfDocument structure={structure} />).toBlob();
  return { blob, filename: proposalFilename(structure.numeroLicitacao, 'pdf') };
};

const declarationStyles = StyleSheet.create({
  reference: { marginBottom: 2 },
  referenceEnd: { marginBottom: 16 },
  paragraph: { marginBottom: 6 },
  listItem: { flexDirection: 'row', marginBottom: 4 },
  listMarker: { width: 18 },
  listText: { flex: 1 },
  underline: { textDecoration: 'underline' },
});

const ALINHAMENTO_PDF: Record<DeclarationAlign, 'left' | 'center' | 'right' | 'justify'> = {
  left: 'left',
  center: 'center',
  right: 'right',
  justify: 'justify',
};

const fonteDo = (trecho: DeclarationRun) => {
  if (trecho.negrito && trecho.italico) return 'Helvetica-BoldOblique';
  if (trecho.negrito) return 'Helvetica-Bold';
  if (trecho.italico) return 'Helvetica-Oblique';
  return 'Helvetica';
};

const DeclarationRuns: React.FC<{ paragrafo: DeclarationParagraph }> = ({ paragrafo }) => (
  <>
    {paragrafo.trechos.map((trecho, index) => (
      <Text
        key={index}
        style={[
          { fontFamily: fonteDo(trecho) },
          trecho.sublinhado ? declarationStyles.underline : {},
        ]}
      >
        {trecho.texto}
      </Text>
    ))}
  </>
);

export const DeclarationPdfDocument: React.FC<{ estrutura: DeclarationStructure }> = ({
  estrutura,
}) => (
  <Document title={estrutura.titulo} creator="ConLicitação">
    <Page size="A4" style={styles.page}>
      {estrutura.referencia.map((campo, index) => (
        <Text
          key={campo.rotulo}
          style={
            index === estrutura.referencia.length - 1
              ? declarationStyles.referenceEnd
              : declarationStyles.reference
          }
        >
          <Text style={styles.bold}>{`${campo.rotulo}: `}</Text>
          {campo.valor}
        </Text>
      ))}
      <PdfDocumentHeader titulo={estrutura.titulo} />
      {estrutura.blocos.map((bloco, index) =>
        bloco.tipo === 'paragrafo' ? (
          <Text
            key={index}
            style={[declarationStyles.paragraph, { textAlign: ALINHAMENTO_PDF[bloco.alinhamento] }]}
          >
            <DeclarationRuns paragrafo={bloco} />
          </Text>
        ) : (
          <View key={index} style={declarationStyles.paragraph}>
            {bloco.itens.map((item, itemIndex) => (
              <View key={itemIndex} style={declarationStyles.listItem}>
                <Text style={declarationStyles.listMarker}>
                  {bloco.ordenada ? `${itemIndex + 1}.` : '•'}
                </Text>
                <Text
                  style={[
                    declarationStyles.listText,
                    { textAlign: ALINHAMENTO_PDF[item.alinhamento] },
                  ]}
                >
                  <DeclarationRuns paragrafo={item} />
                </Text>
              </View>
            ))}
          </View>
        ),
      )}
      <PdfSignatureBlock assinatura={estrutura.assinatura} />
    </Page>
  </Document>
);

export const downloadDeclarationPdf = async (estrutura: DeclarationStructure, nomeBase: string) => {
  const blob = await pdf(<DeclarationPdfDocument estrutura={estrutura} />).toBlob();
  return { blob, filename: `${nomeBase}.pdf` };
};
